import React, { memo, useContext } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import { fireGAEvent, togglePickAction } from '@/analytics';
import { CURRENT_PAGE } from 'nike-ui/src/common/routing';
import { LabelCell, TipCell, TipRow, TipTable } from '../NikeOneCommon';
import BetbookContext from 'nike-ui/src/common/BetbookContext';
import { togglePick } from '@/betslip/handlers';
import { isProfileBetbookSmartViewTipEnabled } from 'nike-ui/src/profiles';
import { nike1Bet$, nike1EditorTipByBetId$ } from '@/nikeone/subs';
import { nike1DetailBet } from '@/nikeone/subs-detail';
import BoxIdContext from 'nike-ui/src/common/BoxIdContext';
import BetLocationContext from 'nike-ui/src/common/BetLocationContext';
import { activeMatchBetHasTicket, activeMatchSelectionHasTicket } from '@/mytickets/active/subs';
import flatten from 'lodash/flatten';
import { getPickId, isPickSelected$$ } from 'nike-ui/src/components/betslip/subs';

const selectionState = {
    WON: 'won',
    LOST: 'lost',
    DISABLED: 'disabled',
    DEFAULT: ''
};
const findIfSelectionContainsLabel = selections =>
    selections !== null && flatten(selections.map(i => i)).filter(selection => selection?.type === 'label').length > 0;

export function Selection({
    selectionsInRow,
    selection,
    betId,
    hiddenBet,
    betNumber,
    narrowCol,
    editorTip,
    hasTicket,
    disableRemovePick,
    selectionDisabled,
    editorTipAlwaysVisible,
    selectionColor,
    showSingleTip,
    initialOdds
}) {
    const { derefSub } = useReframe();
    const betLocationContext = useContext(BetLocationContext);
    const { isBetbook, betbookPostId, betbookBetSelectionCode, betbookPostAuthor } = useContext(BetbookContext);
    const boxId = useContext(BoxIdContext);
    if (!selection || (selection.type === 'none' && selectionsInRow === 1)) {
        return null;
    }
    const odds = selection.odds;
    const tip = selection.tip;
    const winState = selection.winState;
    const { isSelected } = derefSub(isPickSelected$$(getPickId(betId, tip)));
    const selectionHasTicket = hasTicket && derefSub(activeMatchSelectionHasTicket(betId, tip));
    const isInvalid = odds && odds === 1.0;

    // v dialogovom okne zapasu v tikete nechceme dovolit zmazanie picku - len zmenu na iny
    const disabledUnselect = isSelected && disableRemovePick;
    const disabled = (isInvalid && !winState) || disabledUnselect;

    const classes = Object.assign(
        {
            selected: isSelected,
            disabled,
            narrow: narrowCol === selection.col && !isBetbook,
            'on-ticket': selectionHasTicket
        },
        selectionClasses(
            selection,
            true,
            true,
            editorTip,
            editorTipAlwaysVisible,
            selectionColor,
            showSingleTip,
            isBetbook
        )
    );

    const selectionLabel = selection.name;

    function onClick(e) {
        if (!(odds && !winState)) {
            return;
        }
        if (disabled || selectionDisabled) {
            return;
        }

        fireGAEvent(CURRENT_PAGE, togglePickAction(isSelected));
        const pick = {
            betId,
            selectionCode: selection.tip,
            odds,
            infoNumber: betNumber,
            boxId,
            positionId: betLocationContext.betLocationSection?.id,
            positionCode: betLocationContext.betLocationSection?.code
        };
        if (isBetbook && selection.tip === betbookBetSelectionCode.toString()) {
            pick['betbookId'] = betbookPostId;
            pick['betbookContractId'] = betbookPostAuthor;
        }

        e.preventDefault();
        e.stopPropagation();
        dispatch(togglePick(pick));
    }

    if (classes.label) {
        return <LabelCell>{selectionLabel}</LabelCell>;
    }

    return (
        <TipCell
            classes={classes}
            onClick={onClick}
            label={selectionLabel}
            odds={odds}
            locked={isInvalid || hiddenBet}
            initialOdds={initialOdds}
        />
    );
}

function selectionClasses(
    selection,
    showFlagPopular,
    showFlagTop5,
    editorTip,
    betbookActive,
    selectionColor,
    showSingleTip,
    isBetbook
) {
    const { bold, flags, locked, type, winState } = selection;
    const isFlagPopular = showFlagPopular && flags?.includes('Popular');
    const isFlagTop5 = showFlagTop5 && flags?.includes('Top5');
    const isTypeSelection = type === 'selection';
    const bettingAllowed = isTypeSelection && !locked;

    return {
        bold,
        locked: isTypeSelection && locked,
        popular: bettingAllowed && isFlagPopular,
        top5: bettingAllowed && isFlagTop5,
        editorTip: (bettingAllowed || betbookActive) && editorTip,
        overview: editorTip && isProfileBetbookSmartViewTipEnabled() && showSingleTip && isBetbook,
        ripple: bettingAllowed,
        none: type === 'none',
        label: type === 'label',
        selection: isTypeSelection,
        win: winState === 'Win',
        loss: winState === 'Losing',
        selectionUserWon: selectionColor === selectionState.WON && winState === 'Win' && editorTip,
        selectionWon: selectionColor === selectionState.LOST && winState === 'Win',
        disabled: selectionColor === selectionState.DISABLED,
        hide: !editorTip && isProfileBetbookSmartViewTipEnabled() && showSingleTip && isBetbook,
        betbookPickView: isBetbook,
        betbookPickViewBorder: isBetbook && !showSingleTip,
        betbookPickOverView: isBetbook && showSingleTip
    };
}

function SelectionRow({
    row,
    betId,
    hiddenBet,
    betNumber,
    narrowCol,
    hasTicket,
    editorTip,
    betbookActive,
    selectionDisabled,
    editorTipAlwaysVisible,
    selectionColor,
    showSingleTip,
    initialOdds
}) {
    const selectionsInRow = row.length;
    return (
        <TipRow>
            {row.map((selection, key) => (
                <Selection
                    selectionsInRow={selectionsInRow}
                    betId={betId}
                    key={key}
                    editorTip={editorTip && selection && Number(editorTip) === Number(selection.tip)}
                    selection={selection}
                    betNumber={betNumber}
                    hasTicket={hasTicket}
                    narrowCol={narrowCol}
                    betbookActive={betbookActive}
                    selectionDisabled={selectionDisabled}
                    editorTipAlwaysVisible={editorTipAlwaysVisible}
                    selectionColor={selectionColor}
                    showSingleTip={showSingleTip}
                    initialOdds={initialOdds}
                    hiddenBet={hiddenBet}
                />
            ))}
        </TipRow>
    );
}

function Tips({ betId, betSnapshot, boxStore, initialOdds, showEditorTips, showSingleTip, useRaceTemplate, userTip }) {
    const { subscribe } = useReframe();
    const { isBetbook } = useContext(BetbookContext);
    const bet = subscribe(nike1Bet$(boxStore, betId)) || betSnapshot;
    const betNumber = bet.betId;
    const narrowCol = betSnapshot ? null : bet.narrowCol;
    const editorTip = showEditorTips && !userTip ? subscribe(nike1EditorTipByBetId$(boxStore, betId)) : userTip;
    const grid =
        useRaceTemplate || (isBetbook && showSingleTip && findIfSelectionContainsLabel(bet.selectionGrid))
            ? [
                  flatten(bet.selectionGrid)
                      .filter(selection => selection?.type !== 'none')
                      .slice(...(isBetbook ? [] : [0, 3]))
              ]
            : bet.selectionGrid;
    const result = isBetbook && flatten(bet.selectionGrid).some(selection => selection?.type === 'result');
    const state = bet.state || bet.betState;
    const hiddenBet = state === 'Hidden';

    return (
        <TipTable withLabel={grid?.[0]?.[0]?.type === 'label'} result={result}>
            {grid.map((row, i) => (
                <SelectionRow
                    key={i}
                    row={row}
                    betId={betId}
                    hiddenBet={hiddenBet}
                    narrowCol={narrowCol}
                    betNumber={betNumber}
                    editorTip={editorTip}
                    selectionDisabled={hiddenBet}
                    editorTipAlwaysVisible={showEditorTips}
                    showSingleTip={showSingleTip}
                    initialOdds={initialOdds}
                />
            ))}
        </TipTable>
    );
}

export const PrematchDetailBetTips = memo(function PrematchDetailBetTips({ betId, showTickets, betbookActive }) {
    const { subscribe } = useReframe();
    const bet = subscribe(nike1DetailBet(betId));
    const hasTicket = showTickets && subscribe(activeMatchBetHasTicket(betId));
    const betNumber = bet.betNumber;
    const grid = bet.selectionGrid;
    return (
        <TipTable>
            {grid.map((row, i) => (
                <SelectionRow
                    key={i}
                    row={row}
                    betId={betId}
                    hasTicket={hasTicket}
                    betNumber={betNumber}
                    betbookActive={betbookActive}
                />
            ))}
        </TipTable>
    );
});

export default memo(Tips);
